import { useState, useEffect } from 'react';
import { Box, Typography, Tabs, Tab, Paper, CircularProgress, Button } from '@mui/material';
import { onAuthStateChanged, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../../firebase';
import { LicenseService } from '../../sdk/licenseService';
import AppCatalogManager from './AppCatalogManager';
import TemplateManager from './TemplateManager';
import LicenseManager from './LicenseManager';
import PlanDefinitionsManager from './PlanDefinitionsManager';
import PromotionsManager from './PromotionsManager';
import OrganizationsManager from './OrganizationsManager';

const TABS = [
  { label: 'ארגונים', component: <OrganizationsManager /> },
  { label: 'APP-CATALOG', component: <AppCatalogManager /> },
  { label: 'TEMPLATES', component: <TemplateManager /> },
  { label: 'LICENSES', component: <LicenseManager /> },
  { label: 'PLANS', component: <PlanDefinitionsManager /> },
  { label: 'PROMOTIONS', component: <PromotionsManager /> },
];

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [authUser, setAuthUser] = useState(undefined); // undefined = loading
  const [adminUids, setAdminUids] = useState(null);    // null = checking

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthUser(user || null);
    });
    return () => unsubscribe();
  }, []);

  // Once we have an authenticated user, check if they're an admin
  useEffect(() => {
    if (!authUser) {
      setAdminUids(null);
      return;
    }
    LicenseService.getAdminUids()
      .then(uids => setAdminUids(uids))
      .catch(() => setAdminUids([]));
  }, [authUser]);

  const handleLogin = async () => {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  };

  // Loading auth state
  if (authUser === undefined) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Not logged in
  if (!authUser) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
        <Typography variant="h6" color="#1e3a5f">כניסה לפאנל ניהול</Typography>
        <Button variant="contained" onClick={handleLogin} sx={{ bgcolor: '#1e3a5f' }}>
          כניסה עם Google
        </Button>
      </Box>
    );
  }

  // Checking admin status
  if (adminUids === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Not an admin
  if (!adminUids.includes(authUser.uid)) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2, direction: 'rtl' }}>
        <Typography sx={{ fontSize: '2rem' }}>🚫</Typography>
        <Typography variant="h6" color="#1e3a5f" fontWeight={700}>אין גישה</Typography>
        <Typography color="#64748b" fontSize="0.9rem">
          המשתמש שלך ({authUser.email}) אינו מוגדר כ-Super Admin.
        </Typography>
        <Button variant="outlined" onClick={() => auth.signOut()} sx={{ borderColor: '#1e3a5f', color: '#1e3a5f', textTransform: 'none' }}>
          התנתק
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', overflow: 'auto', bgcolor: '#f4f6fa', direction: 'rtl' }}>
      {/* Header */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: '#1e3a5f', color: 'white', px: 4, py: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              פאנל ניהול מערכת
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.5 }}>
              {authUser.email}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => auth.signOut()}
            sx={{ borderColor: 'rgba(255,255,255,0.4)', color: 'rgba(255,255,255,0.8)', textTransform: 'none', fontSize: '0.8rem' }}
          >
            התנתק
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ position: 'sticky', top: 72, zIndex: 9, borderRadius: 0, borderBottom: '1px solid #e0e0e0' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 3,
            '& .MuiTab-root': { fontWeight: 600, fontSize: '0.88rem' },
            '& .Mui-selected': { color: '#1e3a5f' },
            '& .MuiTabs-indicator': { bgcolor: '#1e3a5f' }
          }}
        >
          {TABS.map((tab, i) => (
            <Tab key={i} label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      {/* Content */}
      <Box sx={{ p: 4, maxWidth: 1100, mx: 'auto' }}>
        {TABS[activeTab].component}
      </Box>
    </Box>
  );
};

export default AdminPanel;
